import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Box, CircularProgress, Modal, Typography } from "@mui/material";
import {
  ChevronDown,
  Plus,
  X,
  Search,
  Check,
  Images,
  CircleMinus,
} from "lucide-mui";
import { VISUALIZER_BASE_URL } from "../config/runtime";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { useVisualizer } from "../context/VisualizerContext";
import { listUserImageFiles } from "../api/filesApi";
import { addStepImages, updateStepImages, setStepPrimary } from "../api/stepsApi";
import { visualizeService } from "../api/visualizeService";
import { styledComponent } from "../utils/styled";
import { parseBboxString } from "../utils/boundingBox";
import { buildStepViews, resolveActiveView } from "../utils/stepViews";
import {
  ConfirmDialog,
  PickerModalShell,
  PickerModalHeader,
  PickerModalTitle,
  PickerModalCloseBtn,
  PickerSearchRow,
  PickerSearchBox,
  PickerSearchInput,
  PickerModalFooter,
  PickerSelectedCount,
  PickerFooterBtns,
  PickerCancelBtn,
  PickerActionBtn,
  PickerBtnLabel,
} from "./ui";

const MAX_USER_IMAGES = 25;
const VIEWS_W = 88;
const VIEWS_PAD = 8;
const DOCK_WIDTH = VIEWS_W + VIEWS_PAD * 2;
const THUMB_SIZE = 256;
export const VIEWS_DOCK_WIDTH = DOCK_WIDTH;

// ─── Styled: AddImageModal grid ───────────────────────────────────────────────

const ImageGridBody = styledComponent(Box)(({ isDark }) => ({
  flex: 1,
  overflowY: "auto",
  padding: "12px 32px",
  "&::-webkit-scrollbar": { width: "4px" },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.15)",
    borderRadius: "99px",
  },
}));

const ImageGridWrap = styledComponent(Box)({
  display: "flex",
  flexWrap: "wrap",
  gap: "8px",
});

const GridItemBase = styledComponent(Box)(({ isDark }) => ({
  width: isDark ? "119px" : "136px",
  borderRadius: "8px",
  borderWidth: isDark ? "1.5px" : "2px",
  borderStyle: "solid",
  borderColor: isDark ? "transparent" : "#E5E7EB",
  overflow: "hidden",
  backgroundColor: isDark ? "#222325" : "#F9FAFB",
  transition: "border-color 0.15s",
  position: "relative",
}));

const GridThumbnail = styledComponent(Box)(({ isDark }) => ({
  width: "100%",
  height: isDark ? "72px" : "80px",
  objectFit: "cover",
  display: "block",
  backgroundColor: isDark ? "#3d3e40" : "#EFEFEF",
}));

const GridItemLabelBox = styledComponent(Box)({
  padding: "4px 6px",
});

const GridItemLabel = styledComponent(Typography)(({ isDark }) => ({
  fontFamily: "Inter",
  fontSize: "10px",
  color: isDark ? "rgba(255,255,255,0.7)" : "#6B7280",
  lineHeight: "14px",
}));

const GridSelectedBadge = styledComponent(Box)({
  position: "absolute",
  top: "4px",
  right: "4px",
  width: 18,
  height: 18,
  borderRadius: "50%",
  backgroundColor: "#146E37",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

// ─── Styled: Views dock ───────────────────────────────────────────────────────

const DockRoot = styledComponent(Box)(({ expanded, isDark }) => ({
  flexShrink: 0,
  display: "flex",
  flexDirection: "column",
  height: "100%",
  width: expanded ? DOCK_WIDTH : 0,
  overflow: "hidden",
  background: isDark ? "#212426" : "#F1F4F8",
  borderRight: expanded
    ? isDark
      ? "1px solid #3a3a3a"
      : "1px solid #E5E7EB"
    : "none",
  // When docked, own the shared stage's left corner radius (Unity keeps the right)
  borderRadius: expanded ? "20px 0 0 20px" : 0,
  transition: "width 0.14s ease",
  boxSizing: "border-box",
  zIndex: 5,
}));

const CollapsedChip = styledComponent(Box)(({ isDark, isSidebarOpen }) => ({
  position: "absolute",
  left: isSidebarOpen ? 12 : 52,
  top: 12,
  zIndex: 15,
  display: "flex",
  alignItems: "center",
  gap: 4,
  padding: "0 12px",
  height: 32,
  boxSizing: "border-box",
  borderRadius: 8,
  cursor: "pointer",
  background: isDark
    ? "linear-gradient(135deg, rgba(63, 68, 74, 0.95) 0%, rgba(37, 40, 42, 0.9) 61.3%, #25282A 100%)"
    : "#FFFFFF",
  border: "1px solid",
  borderColor: isDark ? "#363B40" : "#D1D5DC",
  boxShadow: "none",
  color: isDark ? "#fff" : "#434950",
  userSelect: "none",
  pointerEvents: "auto",
}));

/** Collapsed Views control — render inside TocEdit's Unity overlay portal. */
export function ViewsCollapsedChip({ isDark, onClick, isSidebarOpen = true }) {
  return (
    <CollapsedChip isDark={isDark} isSidebarOpen={isSidebarOpen} onClick={onClick}>
      <Images sx={{ width: 16, height: 16, opacity: 0.9 }} />
      <Box component='span' sx={{ fontSize: 11, fontWeight: 400 }}>
        Views
      </Box>
      <ChevronDown sx={{ width: 14, height: 14, opacity: 0.7 }} />
    </CollapsedChip>
  );
}

const ViewsToggle = styledComponent(Box)(({ isDark }) => ({
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "10px 8px",
  color: isDark ? "rgba(255,255,255,0.85)" : "#434950",
  fontSize: 11,
  fontWeight: 400,
  whiteSpace: "nowrap",
  width: "100%",
  textAlign: "left",
  flexShrink: 0,
  cursor: "pointer",
  boxSizing: "border-box",
  "&:hover": {
    background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
  },
}));

const ViewsList = styledComponent(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 6,
  padding: `0 ${VIEWS_PAD}px 10px`,
  width: "100%",
  boxSizing: "border-box",
  overflowY: "auto",
  flex: 1,
  minHeight: 0,
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": { display: "none" },
});

const MediaThumb = styledComponent(Box)(({ active, isDark }) => ({
  position: "relative",
  width: "100%",
  maxWidth: VIEWS_W,
  aspectRatio: "1",
  borderRadius: 8,
  overflow: "hidden",
  border: "2px solid",
  borderColor: active
    ? isDark
      ? "#9A9C9E"
      : "#434950"
    : "transparent",
  background: isDark ? "#2A2A2A" : "#E5E7EB",
  padding: 0,
  flexShrink: 0,
  cursor: "pointer",
  boxSizing: "border-box",
  "&:hover": {
    borderColor: active
      ? isDark
        ? "#9A9C9E"
        : "#434950"
      : isDark
        ? "rgba(255,255,255,0.25)"
        : "#666",
  },
}));

const ThumbImg = styledComponent(Box)({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
});

// Pill hugs content, sits bottom-right; stretches only when text is long.
// Above HoverOverlay so the title stays readable while marquee runs.
const ThumbLabel = styledComponent(Box)({
  position: "absolute",
  right: 4,
  bottom: 4,
  left: "auto",
  maxWidth: "calc(100% - 8px)",
  width: "max-content",
  overflow: "hidden",
  fontSize: 9,
  color: "#fff",
  background: "rgba(0,0,0,0.65)",
  padding: "1px 6px",
  borderRadius: 4,
  pointerEvents: "none",
  fontFamily: "Inter",
  fontWeight: 400,
  lineHeight: "14px",
  boxSizing: "border-box",
  zIndex: 4,
});

const ThumbLabelTrack = styledComponent(Box)({
  display: "inline-flex",
  flexWrap: "nowrap",
  alignItems: "center",
  whiteSpace: "nowrap",
  willChange: "transform",
});

const ThumbLabelSegment = styledComponent(Box)({
  flexShrink: 0,
  whiteSpace: "nowrap",
  lineHeight: "14px",
});

const MARQUEE_GAP_PX = 30;
const MARQUEE_PX_PER_SEC = 28;
const MARQUEE_PAUSE_MS = 2000;

const ActiveBadge = styledComponent(Box)({
  position: "absolute",
  top: 4,
  left: 4,
  width: 18,
  height: 18,
  borderRadius: "50%",
  backgroundColor: "#9A9C9E",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  pointerEvents: "none",
  zIndex: 1,
});

const HoverOverlay = styledComponent(Box)(({ pressed }) => ({
  position: "absolute",
  inset: 0,
  backgroundColor: pressed ? "rgba(30,30,30,0.4)" : "rgba(30, 30, 30, 0.6)",
  borderRadius: 5,
  zIndex: 3,
  transition: "background-color 0.08s ease",
}));

const DeleteButton = styledComponent(Box)({
  position: "absolute",
  top: 4,
  right: 4,
  width: 22,
  height: 22,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  borderRadius: 4,
});

// Top-left on hover — text chip, same vertical band as the delete icon.
const SetPrimaryButton = styledComponent(Box)({
  position: "absolute",
  top: 4,
  left: 4,
  height: 22,
  display: "flex",
  alignItems: "center",
  padding: "0 5px",
  backgroundColor: "#232427",
  borderRadius: 3,
  border: "1px solid rgba(67,73,80,0.5)",
  cursor: "pointer",
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  "&:hover": { backgroundColor: "#2a2d30" },
});

const SetPrimaryText = styledComponent(Typography)({
  fontFamily: "Inter",
  fontSize: 8,
  fontWeight: 500,
  color: "#F4F4F4",
  lineHeight: "12px",
});

const AddImageSlot = styledComponent(Box)(({ isDark }) => ({
  width: "100%",
  maxWidth: VIEWS_W,
  aspectRatio: "1",
  borderRadius: 8,
  border: "1.5px dashed",
  borderColor: isDark ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.18)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 4,
  backgroundColor: "transparent",
  cursor: "pointer",
  flexShrink: 0,
  boxSizing: "border-box",
  "&:hover": {
    borderColor: isDark ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.35)",
    backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
  },
}));

// ─── Add Image Modal ──────────────────────────────────────────────────────────

function AddImageModal({
  open,
  onClose,
  onAdd,
  user_id,
  sessionId,
  existingFileIds = [],
  remainingSlots,
  isSaving = false,
}) {
  const { isDark } = useTheme();
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [brokenThumbnailIds, setBrokenThumbnailIds] = useState(new Set());

  useEffect(() => {
    if (!open) return;
    setSelectedIds(new Set());
    setSearchText("");
    setIsLoading(true);
    listUserImageFiles(user_id, sessionId)
      .then(setFiles)
      .catch(e => console.error("Failed to fetch image files:", e))
      .finally(() => setIsLoading(false));
  }, [open, user_id, sessionId]);

  const filteredFiles = useMemo(() => {
    if (!searchText.trim()) return files;
    const s = searchText.toLowerCase();
    return files.filter(f => f.file_name?.toLowerCase().includes(s));
  }, [files, searchText]);

  const toggleSelect = file_id => {
    if (existingFileIds.includes(file_id)) return;
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(file_id)) {
        next.delete(file_id);
      } else if (next.size < remainingSlots) {
        next.add(file_id);
      }
      return next;
    });
  };

  const handleAdd = () => {
    if (selectedIds.size === 0 || isSaving) return;
    onAdd([...selectedIds]);
  };

  return (
    <Modal
      open={open}
      onClose={isSaving ? undefined : onClose}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <PickerModalShell
        isDark={isDark}
        sx={{ width: "580px", maxHeight: "540px" }}
      >
        <PickerModalHeader isDark={isDark}>
          <PickerModalTitle isDark={isDark}>Add Image</PickerModalTitle>
          <PickerModalCloseBtn
            isDark={isDark}
            onClick={isSaving ? undefined : onClose}
            sx={{ cursor: isSaving ? "default" : "pointer" }}
          >
            <X
              sx={{
                width: 14,
                height: 14,
                color: isDark ? "rgba(255,255,255,0.4)" : "rgba(49,54,59,0.4)",
              }}
            />
          </PickerModalCloseBtn>
        </PickerModalHeader>

        <PickerSearchRow isDark={isDark}>
          <PickerSearchBox isDark={isDark}>
            <Search
              sx={{
                width: 13,
                height: 13,
                color: isDark ? "rgba(255,255,255,0.4)" : "rgba(49,54,59,0.4)",
                flexShrink: 0,
              }}
            />
            <PickerSearchInput
              isDark={isDark}
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              placeholder='Search images…'
            />
          </PickerSearchBox>
        </PickerSearchRow>

        <ImageGridBody isDark={isDark}>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={24} sx={{ color: "#8C8C8C" }} />
            </Box>
          ) : filteredFiles.length === 0 ? (
            <Typography
              sx={{
                textAlign: "center",
                py: 3,
                color: isDark ? "rgba(255,255,255,0.4)" : "rgba(49,54,59,0.4)",
                fontFamily: "Inter",
                fontSize: "13px",
                fontStyle: "italic",
              }}
            >
              No image files found
            </Typography>
          ) : (
            <ImageGridWrap>
              {filteredFiles.map(file => {
                const isSelected = selectedIds.has(file.file_id);
                const isAlreadyAdded = existingFileIds.includes(file.file_id);
                const isAtLimit =
                  !isSelected && selectedIds.size >= remainingSlots;
                const isDisabled = isAlreadyAdded || isAtLimit;
                return (
                  <GridItemBase
                    key={file.file_id}
                    isDark={isDark}
                    onClick={() => !isDisabled && toggleSelect(file.file_id)}
                    sx={{
                      cursor: isDisabled ? "default" : "pointer",
                      opacity: isDisabled ? 0.4 : 1,
                      borderColor: isSelected
                        ? isDark
                          ? "#414447"
                          : "#4C8FFF"
                        : isDark
                          ? "transparent"
                          : "#E5E7EB",
                      "&:hover": !isDisabled
                        ? {
                            borderColor: isSelected
                              ? isDark
                                ? "#414447"
                                : "#4C8FFF"
                              : isDark
                                ? "rgba(255,255,255,0.2)"
                                : "#A8AEB6",
                          }
                        : {},
                    }}
                  >
                    {file.thumbnail_url &&
                    !brokenThumbnailIds.has(file.file_id) ? (
                      <GridThumbnail
                        isDark={isDark}
                        component='img'
                        src={file.thumbnail_url}
                        alt={file.file_name}
                        onError={() =>
                          setBrokenThumbnailIds(
                            prev => new Set(prev).add(file.file_id),
                          )
                        }
                      />
                    ) : (
                      <GridThumbnail isDark={isDark} />
                    )}
                    <GridItemLabelBox>
                      <GridItemLabel isDark={isDark} noWrap>
                        {file.file_name}
                      </GridItemLabel>
                    </GridItemLabelBox>
                    {isSelected && (
                      <GridSelectedBadge>
                        <Check sx={{ width: 11, height: 11, color: "#fff" }} />
                      </GridSelectedBadge>
                    )}
                  </GridItemBase>
                );
              })}
            </ImageGridWrap>
          )}
        </ImageGridBody>

        <PickerModalFooter isDark={isDark}>
          <PickerSelectedCount isDark={isDark}>
            <span>{selectedIds.size}</span>
            <span
              style={{
                color: isDark ? "rgba(255,255,255,0.3)" : "rgba(49,54,59,0.3)",
                marginLeft: "3px",
              }}
            >
              selected
            </span>
          </PickerSelectedCount>
          <PickerFooterBtns>
            <PickerCancelBtn
              isDark={isDark}
              onClick={isSaving ? undefined : onClose}
              sx={{ cursor: isSaving ? "default" : "pointer" }}
            >
              <PickerBtnLabel isDark={isDark}>Cancel</PickerBtnLabel>
            </PickerCancelBtn>
            <PickerActionBtn
              onClick={handleAdd}
              sx={{
                cursor:
                  selectedIds.size > 0 && !isSaving ? "pointer" : "default",
                opacity: selectedIds.size > 0 && !isSaving ? 1 : 0.5,
              }}
            >
              {isSaving ? (
                <CircularProgress size={12} sx={{ color: "#eceff3" }} />
              ) : (
                <PickerBtnLabel isDark={isDark}>Add</PickerBtnLabel>
              )}
            </PickerActionBtn>
          </PickerFooterBtns>
        </PickerModalFooter>
      </PickerModalShell>
    </Modal>
  );
}

// ─── Main ViewsPanel ──────────────────────────────────────────────────────────

const VIEW_3D = "__3d__";

/** Right-aligned one-line name pill; marquees on hover when the label overflows. */
function MarqueeThumbLabel({ children, active }) {
  const clipRef = useRef(null);
  const textRef = useRef(null);
  const [textWidth, setTextWidth] = useState(0);
  const [clipWidth, setClipWidth] = useState(0);
  const [offset, setOffset] = useState(0);
  const [animating, setAnimating] = useState(false);

  const overflow = Math.max(0, textWidth - clipWidth);
  const loopDistance = textWidth + MARQUEE_GAP_PX;

  const measure = useCallback(() => {
    const clip = clipRef.current;
    const text = textRef.current;
    if (!clip || !text) return;
    setClipWidth(clip.clientWidth);
    setTextWidth(text.scrollWidth);
  }, []);

  useEffect(() => {
    measure();
  }, [children, measure]);

  useEffect(() => {
    const clip = clipRef.current;
    if (!clip || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(clip);
    return () => ro.disconnect();
  }, [measure]);

  // Hover: scroll full name → pause 2s → restart. Leave: snap back to start.
  useEffect(() => {
    if (!active || overflow <= 0 || textWidth <= 0) {
      setAnimating(false);
      setOffset(0);
      return undefined;
    }

    let cancelled = false;
    let timeoutId = 0;

    const runCycle = () => {
      if (cancelled) return;
      setAnimating(true);
      setOffset(loopDistance);
      const durationMs = (loopDistance / MARQUEE_PX_PER_SEC) * 1000;
      timeoutId = window.setTimeout(() => {
        if (cancelled) return;
        setAnimating(false);
        timeoutId = window.setTimeout(() => {
          if (cancelled) return;
          setOffset(0);
          // Next frame so the reset isn't animated, then scroll again.
          timeoutId = window.setTimeout(runCycle, 32);
        }, MARQUEE_PAUSE_MS);
      }, durationMs);
    };

    runCycle();

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [active, overflow, textWidth, loopDistance]);

  const scrolling = active && overflow > 0;

  return (
    <ThumbLabel ref={clipRef}>
      <ThumbLabelTrack
        sx={{
          gap: `${MARQUEE_GAP_PX}px`,
          transform: `translateX(-${offset}px)`,
          transition: animating
            ? `transform ${loopDistance / MARQUEE_PX_PER_SEC}s linear`
            : "none",
        }}
      >
        <ThumbLabelSegment ref={textRef}>{children}</ThumbLabelSegment>
        {scrolling && (
          <ThumbLabelSegment aria-hidden='true'>{children}</ThumbLabelSegment>
        )}
      </ThumbLabelTrack>
    </ThumbLabel>
  );
}

const ViewsPanel = ({
  stepData,
  readOnly = false,
  file_id = null,
  currentStep = null,
  sessionId = null,
  anchorId = null,
  dpmUuid = null,
  onDpmUpdate = null,
  onEditBlocked = null,
  onActiveImageChange = null,
  is3D = false,
  expanded: expandedProp = null,
  onExpandedChange = null,
  onDockWidthChange = null,
  onShowCollapsedChange = null,
  captureSignal = 0,
}) => {
  const isExpandControlled = typeof expandedProp === "boolean";
  const [internalExpanded, setInternalExpanded] = useState(false);
  const expanded = isExpandControlled ? expandedProp : internalExpanded;
  const setExpanded = useCallback(
    next => {
      const value = typeof next === "function" ? next(expanded) : next;
      if (!isExpandControlled) setInternalExpanded(value);
      onExpandedChange?.(value);
    },
    [expanded, isExpandControlled, onExpandedChange]
  );
  const [activeViewId, setActiveViewId] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const [pressedId, setPressedId] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [deleteConfirmIndex, setDeleteConfirmIndex] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [thumb3d, setThumb3d] = useState(null);
  const [thumb3dFade, setThumb3dFade] = useState(null);
  const [thumb3dFadeKey, setThumb3dFadeKey] = useState(0);

  const cachedThumb3dRef = useRef(null);
  const thumb3dRef = useRef(null);
  const captureGenRef = useRef(0);
  const activeViewIdRef = useRef(null);
  activeViewIdRef.current = activeViewId;

  const base = (VISUALIZER_BASE_URL || "").replace(/\/$/, "");
  const { user } = useAuth();
  const user_id = user?.id;
  const { isDark } = useTheme();
  const { set2DImage, clear2DImage, show3D, focusImageArea, hideFocusRectangle } =
    useVisualizer();

  const allImages = useMemo(
    () => buildStepViews(stepData, { base, userId: user_id }),
    [stepData?.step_images, stepData?.reference_id, stepData?.reference_type, base, user_id]
  );

  const viewCount = (is3D ? 1 : 0) + allImages.length;
  const canAddMore = !readOnly && allImages.length < MAX_USER_IMAGES;
  const showPanel = viewCount > 0 || canAddMore;

  const doCapture = useCallback(
    async gen => {
      if (activeViewIdRef.current !== VIEW_3D) return;
      const snap = await visualizeService.captureCameraSnapshot(user_id, {
        width: THUMB_SIZE,
        height: THUMB_SIZE,
      });
      if (gen !== captureGenRef.current) return;
      if (activeViewIdRef.current !== VIEW_3D) return;
      if (!snap?.image_base64) return;
      const next = {
        base64: snap.image_base64,
        mime: snap.mime_type || "image/png",
      };
      cachedThumb3dRef.current = next;
      if (!thumb3dRef.current) {
        thumb3dRef.current = next;
        setThumb3d(next);
        return;
      }
      setThumb3dFade(next);
      setThumb3dFadeKey(k => k + 1);
    },
    [user_id]
  );

  const finishThumb3dFade = useCallback(() => {
    const next = cachedThumb3dRef.current;
    if (!next) {
      setThumb3dFade(null);
      return;
    }
    thumb3dRef.current = next;
    setThumb3d(next);
    setThumb3dFade(null);
  }, []);

  const handleSelectImage = useCallback(
    async img => {
      setActiveViewId(img.id);
      try {
        const resolveUrl = img.src.includes("/api/original-image")
          ? img.src + "&url_only=true"
          : null;
        if (resolveUrl) {
          const res = await fetch(resolveUrl);
          const { url } = await res.json();
          set2DImage(`${base}${url}`);
        } else {
          set2DImage(img.src);
        }
      } catch {
        set2DImage(img.src);
      }

      // Per-image focus: prefer bounding_boxes[ref_id]; fall back to flat
      // bounding_box for the primary image (legacy / compiler-generated).
      // Apply after Fetch.image so Unity queues it until the texture is ready.
      const bboxesDict = stepData?.bounding_boxes || {};
      const dictEntry = bboxesDict[String(img.reference_id)];
      const isPrimary =
        String(img.reference_id) === String(stepData?.reference_id) &&
        stepData?.reference_type !== "gltf";
      const bounds = dictEntry
        ? parseBboxString(dictEntry)
        : isPrimary
          ? parseBboxString(stepData?.bounding_box?.[0])
          : null;
      if (bounds) {
        focusImageArea?.(
          bounds.x_min,
          bounds.y_min,
          bounds.x_max,
          bounds.y_max
        );
      } else {
        hideFocusRectangle?.();
        focusImageArea?.();
      }
    },
    [
      base,
      set2DImage,
      focusImageArea,
      hideFocusRectangle,
      stepData?.reference_id,
      stepData?.reference_type,
      stepData?.bounding_box,
      stepData?.bounding_boxes,
    ]
  );

  const handleSelectImageRef = useRef(handleSelectImage);
  handleSelectImageRef.current = handleSelectImage;

  // 3D: compiler auto-adds up to 3 images (4 views w/ 3D) - temporary fix
  // 2D: expand whenever there is more than one view.
  useEffect(() => {
    const next = is3D ? viewCount > 4 : viewCount > 1;
    if (!isExpandControlled) {
      setInternalExpanded(next);
    }
    onExpandedChange?.(next);
    // Only re-seed expand when the step/view inventory changes — not on every expand toggle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, viewCount, is3D]);

  useEffect(() => {
    onDockWidthChange?.(expanded && showPanel ? DOCK_WIDTH : 0);
    return () => onDockWidthChange?.(0);
  }, [expanded, showPanel, onDockWidthChange]);

  // Tell TocEdit when the collapsed chip should appear in the Unity overlay portal.
  useEffect(() => {
    onShowCollapsedChange?.(showPanel && !expanded);
    return () => onShowCollapsedChange?.(false);
  }, [showPanel, expanded, onShowCollapsedChange]);

  // Sync active selection from step reference_*
  useEffect(() => {
    const primary = resolveActiveView(stepData, is3D);
    if (primary?.kind === "gltf") {
      setActiveViewId(VIEW_3D);
      if (is3D) {
        clear2DImage?.();
        show3D?.();
      }
    } else if (primary?.kind === "image") {
      const match = allImages.find(
        img => String(img.reference_id) === String(primary.reference_id)
      );
      setActiveViewId(match?.id ?? allImages[0]?.id ?? (is3D ? VIEW_3D : null));
      if (is3D) {
        if (match) {
          if (activeViewIdRef.current !== match.id) {
            void handleSelectImageRef.current?.(match);
          }
        } else {
          clear2DImage?.();
          show3D?.();
        }
      }
    } else if (is3D) {
      setActiveViewId(VIEW_3D);
      clear2DImage?.();
      show3D?.();
    } else {
      setActiveViewId(allImages[0]?.id ?? null);
    }
    setHoveredId(null);
    setPressedId(null);
  }, [currentStep, stepData?.reference_id, stepData?.reference_type, allImages, is3D, clear2DImage, show3D]);

  // Notify annotation overlays which image slice is active
  useEffect(() => {
    if (!onActiveImageChange) return;
    if (activeViewId === VIEW_3D || activeViewId == null) {
      onActiveImageChange(null);
      return;
    }
    const match = allImages.find(img => img.id === activeViewId);
    onActiveImageChange(match ?? allImages[0] ?? null);
  }, [activeViewId, allImages, onActiveImageChange]);

  useEffect(() => {
    if (!is3D || !showPanel || !user_id) {
      return undefined;
    }

    if (cachedThumb3dRef.current) {
      thumb3dRef.current = cachedThumb3dRef.current;
      setThumb3d(cachedThumb3dRef.current);
    }

    const refType = stepData?.reference_type;
    if (refType && refType !== "gltf") {
      return undefined;
    }

    const gen = ++captureGenRef.current;
    const tEarly = setTimeout(() => {
      doCapture(gen).catch(err =>
        console.warn("3D view thumb capture failed:", err)
      );
    }, 400);
    const tLate = setTimeout(() => {
      doCapture(gen).catch(err =>
        console.warn("3D view thumb capture failed:", err)
      );
    }, 2500);

    return () => {
      clearTimeout(tEarly);
      clearTimeout(tLate);
      captureGenRef.current += 1;
    };
  }, [is3D, showPanel, currentStep, stepData?.reference_type, user_id, doCapture]);

  useEffect(() => {
    if (!is3D || !showPanel || !user_id || captureSignal === 0) return;
    const gen = ++captureGenRef.current;
    doCapture(gen).catch(err =>
      console.warn("3D view thumb capture failed:", err)
    );
  }, [captureSignal, is3D, showPanel, user_id, doCapture]);

  const handleSelect3D = useCallback(() => {
    setActiveViewId(VIEW_3D);
    clear2DImage?.();
    show3D?.();
  }, [clear2DImage, show3D]);

  const handleToggleExpand = () => {
    setExpanded(prev => !prev);
  };

  const saveNewFileIds = async fileIds => {
    if (!file_id || !currentStep || !user_id) return;
    const result = await addStepImages(
      user_id,
      sessionId,
      file_id,
      currentStep,
      fileIds,
      anchorId,
      dpmUuid
    );
    if (result.success && result.dpm_data) {
      onDpmUpdate?.(result.dpm_data);
    } else if (result.reason === "dpm_not_draft") {
      onEditBlocked?.(result, "This DPM can no longer be edited");
    }
  };

  // Reorder/delete gallery only — primary is set via set_step_primary.
  const saveReferenceIds = async images => {
    if (!file_id || !currentStep || !user_id) return;
    const result = await updateStepImages(
      user_id,
      sessionId,
      file_id,
      currentStep,
      images.map(img => img.reference_id),
      anchorId,
      dpmUuid
    );
    if (result.success && result.dpm_data) {
      onDpmUpdate?.(result.dpm_data);
    } else if (result.reason === "dpm_not_draft") {
      onEditBlocked?.(result, "This DPM can no longer be edited");
    }
  };

  const persistPrimary = async payload => {
    if (!file_id || !currentStep || !user_id) return null;
    const result = await setStepPrimary(
      user_id,
      sessionId,
      file_id,
      currentStep,
      payload,
      anchorId,
      dpmUuid
    );
    if (result.success && result.dpm_data) {
      onDpmUpdate?.(result.dpm_data);
    }
    return result;
  };

  const handleAdd = async newFileIds => {
    setIsSaving(true);
    await saveNewFileIds(newFileIds);
    setAddModalOpen(false);
    setIsSaving(false);
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirmIndex === null) return;
    setIsSaving(true);
    const deletedId = allImages[deleteConfirmIndex]?.id;
    const remaining = allImages.filter((_, i) => i !== deleteConfirmIndex);
    await saveReferenceIds(remaining);
    if (activeViewId === deletedId) {
      if (remaining.length > 0) await handleSelectImage(remaining[0]);
      else if (is3D) handleSelect3D();
      else setActiveViewId(null);
    }
    setDeleteConfirmIndex(null);
    setIsSaving(false);
  };

  const handleSetPrimaryImage = async idx => {
    const img = allImages[idx];
    if (!img?.reference_id) return;
    setIsSaving(true);
    const result = await persistPrimary({
      reference_type: img.reference_type || "images",
      reference_id: img.reference_id,
    });
    if (result?.success) await handleSelectImage(img);
    setIsSaving(false);
  };

  const handleSetPrimary3D = async () => {
    setIsSaving(true);
    const result = await persistPrimary({ reference_type: "gltf" });
    if (result?.success) {
      setActiveViewId(VIEW_3D);
      clear2DImage?.();
      show3D?.();
    }
    setIsSaving(false);
  };

  if (!showPanel) return null;

  const primaryView = resolveActiveView(stepData, is3D);
  const isPrimary3D = primaryView?.kind === "gltf";
  const existingFileIds = allImages.map(img => img.file_id).filter(Boolean);

  const renderThumbActions = (isHovered, isPressed, onDelete, onSetPrimary, showPrimary) => {
    if (!isHovered) return null;
    return (
      <HoverOverlay pressed={isPressed}>
        {!readOnly && showPrimary && onSetPrimary && (
          <SetPrimaryButton
            onMouseDown={e => e.stopPropagation()}
            onClick={e => {
              e.stopPropagation();
              onSetPrimary();
            }}
          >
            <SetPrimaryText>Set Primary</SetPrimaryText>
          </SetPrimaryButton>
        )}
        {!readOnly && onDelete && (
          <DeleteButton
            title='Delete'
            onMouseDown={e => e.stopPropagation()}
            onClick={e => {
              e.stopPropagation();
              onDelete();
            }}
          >
            <CircleMinus
              sx={{
                width: 16,
                height: 16,
                color: "#E8E8E8",
                "& svg": { strokeWidth: "2" },
              }}
            />
          </DeleteButton>
        )}
      </HoverOverlay>
    );
  };

  const thumbs = (
    <>
      {is3D && (
        <MediaThumb
          active={activeViewId === VIEW_3D}
          isDark={isDark}
          onMouseEnter={() => setHoveredId(VIEW_3D)}
          onMouseLeave={() => {
            setHoveredId(null);
            setPressedId(null);
          }}
          onMouseDown={() => setPressedId(VIEW_3D)}
          onMouseUp={() => setPressedId(null)}
          onClick={handleSelect3D}
          title='3D Model'
        >
          {thumb3d || thumb3dFade ? (
            <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
              {thumb3d && (
                <ThumbImg
                  component='img'
                  src={`data:${thumb3d.mime};base64,${thumb3d.base64}`}
                  alt='3D'
                  sx={{ position: "absolute", inset: 0 }}
                />
              )}
              {thumb3dFade && (
                <ThumbImg
                  key={thumb3dFadeKey}
                  component='img'
                  src={`data:${thumb3dFade.mime};base64,${thumb3dFade.base64}`}
                  alt=''
                  onAnimationEnd={finishThumb3dFade}
                  sx={{
                    position: "absolute",
                    inset: 0,
                    "@keyframes rtwxThumbFadeIn": {
                      from: { opacity: 0 },
                      to: { opacity: 1 },
                    },
                    animation: "rtwxThumbFadeIn 0.3s ease forwards",
                  }}
                />
              )}
            </Box>
          ) : (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "grid",
                placeItems: "center",
                color: isDark ? "#888" : "#9CA3AF",
                fontSize: 11,
                fontFamily: "Inter",
              }}
            >
              3D
            </Box>
          )}
          {isPrimary3D && hoveredId !== VIEW_3D && (
            <ActiveBadge>
              <Check
                sx={{
                  width: 11,
                  height: 11,
                  color: "#fff",
                  "& svg": { strokeWidth: "3" },
                }}
              />
            </ActiveBadge>
          )}
          <MarqueeThumbLabel active={hoveredId === VIEW_3D}>
            3D
          </MarqueeThumbLabel>
          {renderThumbActions(
            hoveredId === VIEW_3D,
            pressedId === VIEW_3D,
            null,
            handleSetPrimary3D,
            !isPrimary3D
          )}
        </MediaThumb>
      )}

      {allImages.map((img, idx) => {
        const isActive = activeViewId === img.id;
        const isHovered = hoveredId === img.id;
        const isPrimaryImage =
          primaryView?.kind === "image" &&
          String(primaryView.reference_id) === String(img.reference_id);

        return (
          <MediaThumb
            key={img.id}
            active={isActive}
            isDark={isDark}
            onMouseEnter={() => setHoveredId(img.id)}
            onMouseLeave={() => {
              setHoveredId(null);
              setPressedId(null);
            }}
            onMouseDown={() => setPressedId(img.id)}
            onMouseUp={() => setPressedId(null)}
            onClick={() => handleSelectImage(img)}
          >
            <ThumbImg component='img' src={img.src} alt={img.label} />
            {isPrimaryImage && !isHovered && (
              <ActiveBadge>
                <Check
                  sx={{
                    width: 11,
                    height: 11,
                    color: "#fff",
                    "& svg": { strokeWidth: "3" },
                  }}
                />
              </ActiveBadge>
            )}
            <MarqueeThumbLabel active={isHovered}>{img.label}</MarqueeThumbLabel>
            {renderThumbActions(
              isHovered,
              pressedId === img.id,
              () => setDeleteConfirmIndex(idx),
              () => handleSetPrimaryImage(idx),
              !isPrimaryImage && allImages.length > 0
            )}
          </MediaThumb>
        );
      })}

      {canAddMore && (
        <AddImageSlot
          isDark={isDark}
          onClick={() => setAddModalOpen(true)}
          title='Add Image'
        >
          <Plus
            sx={{
              width: 18,
              height: 18,
              color: isDark ? "#B3B3BB" : "#6B7280",
            }}
          />
        </AddImageSlot>
      )}
    </>
  );

  return (
    <>
      <DockRoot expanded={expanded} isDark={isDark}>
        {expanded && (
          <>
            <ViewsToggle
              isDark={isDark}
              onClick={handleToggleExpand}
              aria-expanded='true'
            >
              <Images sx={{ width: 14, height: 14, opacity: 0.9 }} />
              <Box component='span' sx={{ flex: 1 }}>
                Views
              </Box>
              <ChevronDown
                sx={{
                  width: 12,
                  height: 12,
                  opacity: 0.7,
                  transform: "rotate(180deg)",
                }}
              />
            </ViewsToggle>
            <ViewsList>{thumbs}</ViewsList>
          </>
        )}
      </DockRoot>

      <AddImageModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onAdd={handleAdd}
        user_id={user_id}
        sessionId={sessionId}
        existingFileIds={existingFileIds}
        remainingSlots={MAX_USER_IMAGES - allImages.length}
        isSaving={isSaving}
      />
      <ConfirmDialog
        danger
        isDark={isDark}
        title='Delete Image?'
        message='Are you sure you want to delete this image?'
        confirmLabel='Delete'
        open={deleteConfirmIndex !== null}
        onCancel={() => setDeleteConfirmIndex(null)}
        onConfirm={handleDeleteConfirm}
        isLoading={isSaving}
      />
    </>
  );
};

export default ViewsPanel;
